import Cookies from 'js-cookie'
import ky from 'ky'
import type {CountryCode} from 'libphonenumber-js'

import {paramCookiesMap} from '@helios/website/src/config/cookies'
import type {ConsentsPayload} from '@helios/website/src/types/consents'
import type {HubspotContactProps} from '@helios/website/src/types/hubspot-contact-props'
import type {CompanySize, HubspotFormData} from '@helios/website/src/types/hubspot-form-fields'

export interface HubspotState {
    firstName?: string
    lastName?: string
    email: string
    genericEmail: boolean
    phoneNumber?: string
    phoneCountryCode?: CountryCode
    company?: string
    companySize?: CompanySize | ''
    country?: HubspotContactProps['country']
    hasConsented?: boolean
    marketingEmails?: boolean
    lifecyclestage?: HubspotContactProps['lifecyclestage']
    event_name?: HubspotContactProps['event_name']
    latest_campaign?: HubspotContactProps['latest_campaign']
    lead_source_latest_capture_type?: HubspotContactProps['lead_source_latest_capture_type']
    pleo_partner_id?: HubspotContactProps['pleo_partner_id']
    partner_channel?: HubspotContactProps['partner_channel']
    type_of_influence?: HubspotContactProps['type_of_influence']
    partner_segment?: HubspotContactProps['partner_segment']
    partnership_type?: HubspotContactProps['partnership_type']
    partnership_referred_date?: HubspotContactProps['partnership_referred_date']
    type_of_lead?: HubspotContactProps['type_of_lead']
    message?: HubspotContactProps['message']
    pdfUrl?: string
    associated_partner_id?: HubspotContactProps['associated_partner_id']
    jobTitle?: HubspotContactProps['jobtitle']
    featuresOfInterest?: HubspotContactProps['features_of_interest']
    monthlySpend?: HubspotContactProps['monthly_business_spend']
    accountingSystem?: HubspotContactProps['accounting_system']
}

export const submitHubspotForm = async (
    captchaResponse: string,
    state: HubspotState,
    formId: string,
    consent?: ConsentsPayload,
) => {
    const consents = {
        legalConsentOptions: {
            consent,
        },
    }

    const hutk = Cookies.get(paramCookiesMap.hubspotToken)

    const formData: HubspotFormData = {
        fields: [
            {
                objectTypeId: '0-1',
                name: 'email',
                value: state?.email || '',
            },
            {
                objectTypeId: '0-1',
                name: 'firstname',
                value: state?.firstName || '',
            },
            {
                objectTypeId: '0-1',
                name: 'lastname',
                value: state?.lastName || '',
            },
            {
                objectTypeId: '0-1',
                name: 'phone',
                value: state?.phoneNumber || '',
            },
            {
                objectTypeId: '0-1',
                name: 'country',
                value: state?.country || '',
            },
            {
                objectTypeId: '0-1',
                name: 'company_size_range',
                value: state?.companySize || '',
            },
            {
                objectTypeId: '0-1',
                name: 'company',
                value: state?.company || '',
            },
            {
                objectTypeId: '0-1',
                name: 'hs_legal_basis',
                value: state?.marketingEmails ? 'Freely given consent from contact' : '',
            },
            {
                objectTypeId: '0-1',
                name: 'hs_linkedin_click_id',
                value: Cookies.get(paramCookiesMap.linkedinClickId) || '',
            },
            {
                objectTypeId: '0-1',
                name: 'utm_source',
                value: Cookies.get(paramCookiesMap.utmSource) || '',
            },
            {
                objectTypeId: '0-1',
                name: 'utm_medium',
                value: Cookies.get(paramCookiesMap.utmMedium) || '',
            },
            {
                objectTypeId: '0-1',
                name: 'utm_campaign',
                value: Cookies.get(paramCookiesMap.utmCampaign) || '',
            },
            {
                objectTypeId: '0-1',
                name: 'utm_term',
                value: Cookies.get(paramCookiesMap.utmTerm) || '',
            },
            {
                objectTypeId: '0-1',
                name: 'utm_content',
                value: Cookies.get(paramCookiesMap.utmContent) || '',
            },
            {
                objectTypeId: '0-1',
                name: 'latest_utm_source',
                value: Cookies.get(paramCookiesMap.latestUtmSource) || '',
            },
            {
                objectTypeId: '0-1',
                name: 'latest_utm_medium',
                value: Cookies.get(paramCookiesMap.latestUtmMedium) || '',
            },
            {
                objectTypeId: '0-1',
                name: 'latest_utm_campaign',
                value: Cookies.get(paramCookiesMap.latestUtmCampaign) || '',
            },
            {
                objectTypeId: '0-1',
                name: 'latest_utm_term',
                value: Cookies.get(paramCookiesMap.latestUtmTerm) || '',
            },
            {
                objectTypeId: '0-1',
                name: 'latest_utm_content',
                value: Cookies.get(paramCookiesMap.latestUtmContent) || '',
            },
            {
                objectTypeId: '0-1',
                name: 'referral_id',
                value: Cookies.get(paramCookiesMap.latestReferralCode) || '',
            },
            {
                objectTypeId: '0-1',
                name: 'lifecyclestage',
                value: state?.lifecyclestage || '',
            },
            {
                objectTypeId: '0-1',
                name: 'event_name',
                value: state?.event_name || '',
            },
            {
                objectTypeId: '0-1',
                name: 'latest_campaign',
                value: state?.latest_campaign || '',
            },
            {
                objectTypeId: '0-1',
                name: 'lead_source_latest_capture_type',
                value: state?.lead_source_latest_capture_type || '',
            },
            {
                objectTypeId: '0-1',
                name: 'pleo_partner_id',
                value: state?.pleo_partner_id || '',
            },
            {
                objectTypeId: '0-1',
                name: 'associated_partner_id',
                value: state?.associated_partner_id || '',
            },
            {
                objectTypeId: '0-1',
                name: 'partner_channel',
                value: state?.partner_channel || '',
            },
            {
                objectTypeId: '0-1',
                name: 'type_of_influence',
                value: state?.type_of_influence || '',
            },
            {
                objectTypeId: '0-1',
                name: 'partner_segment',
                value: state?.partner_segment || '',
            },
            {
                objectTypeId: '0-1',
                name: 'partnership_type',
                value: state?.partnership_type || '',
            },
            {
                objectTypeId: '0-1',
                name: 'partnership_referred_date',
                value: state?.partnership_referred_date || '',
            },
            {
                objectTypeId: '0-1',
                name: 'type_of_lead',
                value: state?.type_of_lead || '',
            },
            {
                objectTypeId: '0-1',
                name: 'message',
                value: state?.message || '',
            },
            {
                objectTypeId: '0-1',
                name: 'pdf_url',
                value: state?.pdfUrl || '',
            },
            {
                objectTypeId: '0-1',
                name: 'jobtitle',
                value: state?.jobTitle || '',
            },
            {
                objectTypeId: '0-1',
                name: 'latest_segment_anonymous_id',
                value: window.analytics?.user?.()?.anonymousId?.() || '',
            },
            {
                objectTypeId: '0-2',
                name: 'features_of_interest',
                value: state?.featuresOfInterest || '',
            },
            {
                objectTypeId: '0-2',
                name: 'accounting_system',
                value: state?.accountingSystem || '',
            },
            {
                objectTypeId: '0-2',
                name: 'monthly_business_spend',
                value: state?.monthlySpend || '',
            },
        ],
        context: {
            hutk,
            pageUri: window?.location.href || '',
            pageName: document?.title || '',
        },
        formId,
        ...(consent && consents),
    }

    return await ky
        .post('/api/hubspot-submit-form', {
            json: {formData, captchaResponse},
            throwHttpErrors: true,
        })
        .json()
}
